import React from "react";
import ModalNew from "../../../../components/Modal/ModalNew";
import { XCircle, RefreshCcw } from "lucide-react";

interface PaymentFailedModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessage?: string;
  onRetry?: () => void;
}

const PaymentFailedModal: React.FC<PaymentFailedModalProps> = ({
  isOpen,
  onClose,
  errorMessage,
  onRetry,
}) => {
  const handleRetry = () => {
    onClose();
    if (onRetry) {
      onRetry();
    }
  };

  return (
    <ModalNew
      isOpen={isOpen}
      onClose={onClose}
      title=""
      className="max-w-[500px] mx-auto"
    >
      <div className="text-center py-6 px-4">
        <div className="flex flex-col items-center">
          {/* Error Icon */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-red-400 to-red-600 flex items-center justify-center mb-6">
            <XCircle className="w-12 h-12 text-white" />
          </div>

          {/* Error Message */}
          <h3 className="text-[24px] md:text-[32px] font-bold mb-3 text-[#212121]">
            Payment Failed
          </h3>

          <p className="text-gray-600 text-[16px] md:text-[18px] mb-6 max-w-md">
            {errorMessage ||
              "We couldn't process your payment. Please try again or contact support if the problem persists."}
          </p>

          {/* Common Reasons */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 w-full max-w-sm text-left">
            <p className="font-semibold text-gray-800 mb-2 text-sm">
              Common reasons for failure:
            </p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Insufficient funds</li>
              <li>Incorrect payment details</li>
              <li>Network connectivity issues</li>
              <li>Card expired or blocked</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
            {onRetry && (
              <button
                onClick={handleRetry}
                className="flex-1 bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <RefreshCcw className="w-4 h-4" />
                Try Again
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all"
            >
              Close
            </button>
          </div>

          {/* Contact Support */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 mb-2">Need help?</p>
            <a
              href="mailto:solutions@25thmail.com"
              className="text-[#0B60D6] font-semibold hover:underline text-sm"
            >
              Contact Support
            </a>
            <span className="text-gray-400 mx-2">|</span>
            <a
              href="tel:+2347068275230"
              className="text-[#0B60D6] font-semibold hover:underline text-sm"
            >
              Call Us
            </a>
          </div>
        </div>
      </div>
    </ModalNew>
  );
};

export default PaymentFailedModal;
